import { useState } from 'react';
import type { LayoutChangeEvent } from 'react-native';
import { View, XStack } from 'tamagui';

const GAP = 4;
const LINE_HEIGHT = 4;

export const WorkflowStepIndicator = ({
    currentStepIndex = -1,
    totalStepCount,
    inactiveColor = '$gray400',
    activeColor = '$black',
    segmentWidthOverride,
    segmentGapOverride,
}: {
    currentStepIndex: number | undefined;
    totalStepCount: number;
    inactiveColor?: string;
    activeColor?: string;
    segmentWidthOverride?: number;
    segmentGapOverride?: number;
}) => {
    const [containerWidth, setContainerWidth] = useState(0);

    const invisible = currentStepIndex == null || currentStepIndex < 0;

    let stepIndex = currentStepIndex ?? -1;
    if (stepIndex >= totalStepCount) {
        stepIndex = totalStepCount - 1;
    }

    const segmentGap = segmentGapOverride ?? GAP;

    const segmentWidth =
        segmentWidthOverride ??
        (containerWidth > 0 && totalStepCount > 0
            ? (containerWidth - segmentGap * (totalStepCount - 1)) /
              totalStepCount
            : 0);

    const segmentsRowWidth =
        segmentWidthOverride != null && totalStepCount > 0
            ? totalStepCount * segmentWidth + segmentGap * (totalStepCount - 1)
            : containerWidth;

    const canRender =
        !invisible &&
        segmentWidth > 0 &&
        (segmentWidthOverride != null || containerWidth > 0);

    const handleLayout = (event: LayoutChangeEvent) => {
        setContainerWidth(event.nativeEvent.layout.width);
    };

    return (
        <View
            height={LINE_HEIGHT}
            width="100%"
            alignSelf="stretch"
            onLayout={handleLayout}
        >
            {canRender && (
                <XStack width="100%" justifyContent="center">
                    <View
                        width={segmentsRowWidth}
                        height={LINE_HEIGHT}
                        position="relative"
                    >
                        <XStack gap={segmentGap}>
                            {Array.from({ length: totalStepCount }, (_, i) => (
                                <View
                                    key={i}
                                    flex={segmentWidthOverride != null ? 0 : 1}
                                    height={LINE_HEIGHT}
                                    borderRadius={100}
                                    backgroundColor={inactiveColor}
                                    width={
                                        segmentWidthOverride != null
                                            ? segmentWidth
                                            : undefined
                                    }
                                />
                            ))}
                        </XStack>
                        <View
                            position="absolute"
                            top={0}
                            left={stepIndex * (segmentWidth + segmentGap)}
                            transform={[{ translateY: 0 }]}
                            enterStyle={{
                                opacity: 0,
                                transform: [{ translateY: 4 }],
                            }}
                            exitStyle={{
                                opacity: 0,
                                transform: [{ translateY: 4 }],
                            }}
                            height={LINE_HEIGHT}
                            width={segmentWidth}
                            borderRadius={100}
                            backgroundColor={activeColor}
                            animateOnly={['opacity', 'transform']}
                            animation="bouncy"
                            pointerEvents="none"
                        />
                    </View>
                </XStack>
            )}
        </View>
    );
};
